import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Check, ArrowDownToLine, Wallet, X, QrCode, BellOff, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import api from '../services/api';
import CryptoIcon from './CryptoIcon';

/**
 * Mobile Deposit card with:
 *   - Two-tap QR sheet (tap card -> scan / copy)
 *   - BIP21-style "specify amount" chip inside the QR sheet
 *   - Optional "Complete your funding" nudge banner shown if the user
 *     hasn't deposited in >= 14 days (or has never deposited).
 */
const NETWORK_PRIORITY = ['TRC20', 'ERC20', 'BEP20', 'Solana', 'Polygon', 'Bitcoin'];
const SUPPORTED_CRYPTOS = ['BTC', 'ETH', 'USDT', 'USDC'];
const SWIPE_CLOSE_THRESHOLD = 90;
const AMOUNT_PRESETS = [50, 100, 500, 1000];

// BIP21-style URI per (crypto, network). Not every wallet parses these but the
// address alone remains scannable, so an amount hint costs nothing.
const buildUri = (crypto, network, address, amount) => {
  const amt = Number(amount);
  if (!address) return '';
  const hasAmt = Number.isFinite(amt) && amt > 0;
  const q = hasAmt ? `?amount=${amt}` : '';
  // Native chains
  if (crypto === 'BTC' || network === 'Bitcoin') {
    return `bitcoin:${address}${q}`;
  }
  if (crypto === 'ETH' && network === 'ERC20') {
    return `ethereum:${address}${hasAmt ? `?value=${amt}` : ''}`;
  }
  // Token-on-chain URIs
  const tokenParam = crypto === 'BTC' ? '' : `&token=${crypto}`;
  switch (network) {
    case 'ERC20':
      return `ethereum:${address}${q}${hasAmt ? tokenParam : ''}`;
    case 'BEP20':
      return `binance:${address}${q}${hasAmt ? tokenParam : ''}`;
    case 'TRC20':
      return `tron:${address}${q}${hasAmt ? tokenParam : ''}`;
    case 'Solana':
      return `solana:${address}${q}${hasAmt && crypto !== 'BTC' ? `&spl-token=${crypto}` : ''}`;
    case 'Polygon':
      return `polygon:${address}${q}${hasAmt ? tokenParam : ''}`;
    default:
      return `${address}${hasAmt ? ` (${amt} ${crypto})` : ''}`;
  }
};

const pickBest = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  for (const net of NETWORK_PRIORITY) {
    if (obj[net]) return { network: net, address: obj[net] };
  }
  for (const [net, addr] of Object.entries(obj)) {
    if (addr) return { network: net, address: addr };
  }
  return null;
};

const CopyDepositAddressCard = () => {
  const [addresses, setAddresses] = useState(null); // { BTC: {Bitcoin: '...'}, USDT: {TRC20: '...', ERC20: '...'}, ... }
  const [best, setBest] = useState(null);           // Default USDT preview shown on card
  const [selectedCrypto, setSelectedCrypto] = useState('USDT');
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [amount, setAmount] = useState('');
  const [dragOffset, setDragOffset] = useState(0);
  const [nudge, setNudge] = useState(null);
  const [snoozing, setSnoozing] = useState(false);

  const dragOffsetRef = useRef(0);
  const touchStartYRef = useRef(null);

  // Fetch all deposit addresses (user override merged over platform)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/deposit-addresses/all');
        if (cancelled) return;
        const platform = data?.platform_addresses || {};
        const userAddrs = data?.user_addresses || {};
        // Deep-merge with user override winning per (crypto, network)
        const merged = JSON.parse(JSON.stringify(platform));
        for (const c of Object.keys(userAddrs)) {
          if (!userAddrs[c] || typeof userAddrs[c] !== 'object') continue;
          if (!merged[c]) merged[c] = {};
          for (const net of Object.keys(userAddrs[c])) {
            if (userAddrs[c][net] && String(userAddrs[c][net]).trim()) {
              merged[c][net] = userAddrs[c][net];
            }
          }
        }
        setAddresses(merged);
        // Default preview on the card is USDT (best network), fall back to first crypto
        const usdtBest = pickBest(merged.USDT);
        if (usdtBest) {
          setBest(usdtBest);
          setSelectedCrypto('USDT');
          setSelectedNetwork(usdtBest.network);
        } else {
          for (const c of SUPPORTED_CRYPTOS) {
            if (merged[c]) {
              const b = pickBest(merged[c]);
              if (b) {
                setBest(b);
                setSelectedCrypto(c);
                setSelectedNetwork(b.network);
                break;
              }
            }
          }
        }
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Derived: available networks for the currently-selected crypto
  const availableNetworks = addresses?.[selectedCrypto]
    ? Object.keys(addresses[selectedCrypto]).filter((n) => addresses[selectedCrypto][n])
    : [];

  const currentAddress = selectedNetwork && addresses?.[selectedCrypto]?.[selectedNetwork]
    ? addresses[selectedCrypto][selectedNetwork]
    : '';

  // Preview shown on the card face itself — prefer ETH's ERC20 address so
  // users can see the ETH-network deposit address at a glance (as requested).
  // Falls back to USDT-on-ERC20 if the standalone ETH address isn't configured.
  const ethPreview = (() => {
    const eth = addresses?.ETH?.ERC20 || addresses?.ETH?.ethereum;
    if (eth) return { crypto: 'ETH', address: eth };
    const usdtErc20 = addresses?.USDT?.ERC20;
    if (usdtErc20) return { crypto: 'USDT', address: usdtErc20 };
    return null;
  })();

  const truncateAddress = (addr) => {
    if (!addr) return '';
    return addr.length > 14 ? `${addr.slice(0, 8)}…${addr.slice(-6)}` : addr;
  };

  const handleCopyEth = async (e) => {
    e.stopPropagation();
    if (!ethPreview?.address) return;
    try {
      await navigator.clipboard.writeText(ethPreview.address);
      toast.success(`ERC20 ${ethPreview.crypto} address copied`);
    } catch {
      toast.error('Could not copy — long-press to select');
    }
  };

  // When user picks a different crypto, jump to that crypto's best network
  const handleCryptoChange = (c) => {
    setSelectedCrypto(c);
    const b = pickBest(addresses?.[c]);
    setSelectedNetwork(b?.network || null);
  };

  // Fetch deposit nudge
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/dashboard/deposit-nudge');
        if (!cancelled) setNudge(data || null);
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // (Re)generate QR when we open the sheet OR the crypto/network/amount changes
  useEffect(() => {
    if (!open || !currentAddress) return;
    const uri = buildUri(selectedCrypto, selectedNetwork, currentAddress, amount);
    QRCode.toDataURL(uri || currentAddress, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 320,
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [open, selectedCrypto, selectedNetwork, currentAddress, amount]);

  // Modal chrome: Escape / body-lock / history-back
  useEffect(() => {
    if (!open) return undefined;
    const closeIt = () => { setOpen(false); setDragOffset(0); dragOffsetRef.current = 0; };
    const onKey = (e) => { if (e.key === 'Escape') closeIt(); };
    window.history.pushState({ depositQrOpen: true }, '');
    const onPop = () => closeIt();
    window.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      document.body.style.overflow = prev;
      if (window.history.state && window.history.state.depositQrOpen) {
        window.history.back();
      }
    };
  }, [open]);

  const openSheet = () => setOpen(true);
  const closeSheet = () => {
    setOpen(false);
    setDragOffset(0);
    dragOffsetRef.current = 0;
  };

  const handleCopy = async () => {
    if (!currentAddress) return;
    try {
      await navigator.clipboard.writeText(currentAddress);
      setCopied(true);
      toast.success(`${selectedCrypto} address copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — long-press to select');
    }
  };

  const handleSnooze = async () => {
    setSnoozing(true);
    try {
      await api.post('/dashboard/deposit-nudge/snooze');
      setNudge((n) => ({ ...(n || {}), should_show: false }));
      toast.success('We\u2019ll remind you again in a few days');
    } catch {
      toast.error('Could not snooze — try again');
    } finally {
      setSnoozing(false);
    }
  };

  // Swipe-down handlers
  const onTouchStart = (e) => {
    touchStartYRef.current = e.touches[0].clientY;
    dragOffsetRef.current = 0;
    setDragOffset(0);
  };
  const onTouchMove = (e) => {
    if (touchStartYRef.current == null) return;
    const dy = e.touches[0].clientY - touchStartYRef.current;
    if (dy > 0) { dragOffsetRef.current = dy; setDragOffset(dy); }
  };
  const onTouchEnd = () => {
    if (dragOffsetRef.current > SWIPE_CLOSE_THRESHOLD) closeSheet();
    else { dragOffsetRef.current = 0; setDragOffset(0); }
    touchStartYRef.current = null;
  };

  if (loading) return null;

  const anyAddress = addresses && Object.values(addresses).some(
    (nets) => nets && Object.values(nets).some((a) => a)
  );

  if (!anyAddress) {
    return (
      <div
        className="md:hidden mb-4 sm:mb-6 rounded-2xl p-4 bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/20"
        data-testid="deposit-card-empty"
      >
        <div className="flex items-center gap-3">
          <div className="shrink-0 p-2.5 rounded-xl bg-purple-500/20">
            <Wallet className="h-5 w-5 text-purple-300" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-white text-sm">Deposit address unavailable</p>
            <p className="text-gray-400 text-xs mt-0.5">Contact support to set up your USDT wallet.</p>
          </div>
        </div>
      </div>
    );
  }

  const showNudge = Boolean(nudge?.should_show);
  const days = nudge?.days_since_last;
  const isFirstTime = nudge?.reason === 'first_time';

  const nudgeTitle = isFirstTime ? 'Complete your funding' : 'Time to top up';
  const nudgeSub = isFirstTime
    ? 'Fund your wallet to start trading — it takes seconds.'
    : `It\u2019s been ${days}+ days since your last deposit. Top up to keep trading.`;

  const amountNumber = Number(amount);
  const amountIsValid = Number.isFinite(amountNumber) && amountNumber > 0;

  return (
    <>
      {showNudge && (
        <div
          className="md:hidden mb-3 rounded-2xl p-4 bg-gradient-to-br from-amber-500/20 via-orange-500/15 to-rose-500/20 border border-amber-400/40 shadow-[0_0_24px_rgba(251,191,36,0.15)]"
          data-testid="deposit-nudge-banner"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 p-2 rounded-xl bg-amber-500/25">
              <Sparkles className="h-5 w-5 text-amber-200" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-white text-sm">{nudgeTitle}</p>
              <p className="text-amber-100/80 text-xs mt-0.5 leading-snug" data-testid="deposit-nudge-sub">
                {nudgeSub}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={openSheet}
                  className="px-3 py-1.5 rounded-lg bg-amber-400 hover:bg-amber-300 text-amber-950 text-xs font-bold focus:outline-none"
                  data-testid="deposit-nudge-cta-btn"
                >
                  Deposit now
                </button>
                <button
                  type="button"
                  onClick={handleSnooze}
                  disabled={snoozing}
                  className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-amber-100/70 text-xs font-medium inline-flex items-center gap-1 focus:outline-none disabled:opacity-50"
                  data-testid="deposit-nudge-snooze-btn"
                  aria-label="Remind me later"
                >
                  <BellOff className="h-3 w-3" />
                  Remind me later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={openSheet}
        className="md:hidden mb-4 sm:mb-6 w-full text-left rounded-2xl p-4 bg-gradient-to-br from-purple-900/40 via-indigo-900/30 to-purple-900/40 border border-purple-500/25 shadow-lg hover:border-purple-500/50 transition-colors focus:outline-none"
        data-testid="deposit-card"
        aria-label="Open deposit QR code"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="shrink-0 flex -space-x-2">
              {SUPPORTED_CRYPTOS.map((c) => (
                <CryptoIcon key={c} symbol={c} size={28} className="ring-2 ring-[#0a1628]" />
              ))}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-bold text-white text-sm">Deposit crypto</p>
                {best?.network && (
                  <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-semibold uppercase tracking-wide">
                    {best.network}
                  </span>
                )}
              </div>
              <p className="text-gray-300 text-xs mt-0.5 truncate" data-testid="deposit-card-address">
                BTC · ETH · USDT · USDC
              </p>
            </div>
          </div>
          <div className="shrink-0 px-3 py-2 rounded-xl bg-purple-500 text-white flex items-center gap-1.5 text-sm font-semibold">
            <QrCode className="h-4 w-4" />
            <span>Scan</span>
          </div>
        </div>
        {ethPreview?.address && (
          <div
            className="mt-3 flex items-center gap-2 rounded-xl bg-black/25 border border-purple-500/20 px-3 py-2"
            data-testid="deposit-card-eth-preview"
          >
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold uppercase tracking-wide shrink-0">
              ERC20
            </span>
            <p
              className="text-white text-[11px] font-mono flex-1 truncate"
              data-testid="deposit-card-eth-address"
              title={ethPreview.address}
            >
              {truncateAddress(ethPreview.address)}
            </p>
            <span
              role="button"
              tabIndex={0}
              onClick={handleCopyEth}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCopyEth(e); } }}
              className="shrink-0 p-1.5 rounded-md bg-purple-500/80 hover:bg-purple-500 text-white focus:outline-none cursor-pointer"
              aria-label="Copy ETH network address"
              data-testid="deposit-card-eth-copy-btn"
            >
              <Copy className="h-3 w-3" />
            </span>
          </div>
        )}
        <p className="mt-3 text-[11px] text-gray-400 flex items-center gap-1.5">
          <ArrowDownToLine className="h-3 w-3 shrink-0" />
          Tap to pick a coin, network, and view the QR.
        </p>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm"
          onClick={closeSheet}
          role="presentation"
          data-testid="deposit-qr-overlay"
        >
          <div
            className="absolute bottom-0 left-0 right-0 md:bottom-auto md:top-1/2 md:right-auto md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[440px] md:max-w-[calc(100vw-2rem)] md:rounded-2xl bg-[#0a1628] border border-purple-500/25 rounded-t-3xl shadow-2xl overflow-hidden"
            style={{
              paddingBottom: 'env(safe-area-inset-bottom)',
              transform: dragOffset ? `translateY(${dragOffset}px)` : undefined,
              transition: dragOffset ? 'none' : 'transform 200ms ease-out',
              maxHeight: '92vh',
            }}
            onClick={e => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            role="dialog"
            aria-modal="true"
            aria-labelledby="deposit-qr-title"
            data-testid="deposit-qr-sheet"
          >
            {/* Drag handle — mobile only */}
            <div className="md:hidden mx-auto mt-3 h-1.5 w-12 rounded-full bg-gray-600" aria-hidden="true" />

            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-2 min-w-0">
                <CryptoIcon symbol={selectedCrypto} size={28} className="shrink-0" />
                <div>
                  <h3 id="deposit-qr-title" className="text-white font-bold text-base leading-tight">Deposit {selectedCrypto}</h3>
                  {selectedNetwork && (
                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-semibold uppercase tracking-wide" data-testid="deposit-qr-network-pill">
                      {selectedNetwork} network
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={closeSheet}
                className="p-2 rounded-lg hover:bg-white/5 focus:outline-none"
                aria-label="Close QR sheet"
                data-testid="deposit-qr-close-btn"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="px-5 overflow-y-auto" style={{ maxHeight: 'calc(92vh - 72px)' }}>
              {/* Crypto selector */}
              <div className="mb-3">
                <p className="text-gray-400 text-xs mb-2">Coin</p>
                <div className="flex flex-wrap items-center gap-2" data-testid="deposit-qr-crypto-controls">
                  {SUPPORTED_CRYPTOS.map((c) => {
                    const available = Boolean(addresses?.[c] && Object.keys(addresses[c]).some((n) => addresses[c][n]));
                    const active = selectedCrypto === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => available && handleCryptoChange(c)}
                        disabled={!available}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors focus:outline-none inline-flex items-center gap-1.5 ${
                          active
                            ? 'bg-purple-500 border-purple-500 text-white'
                            : available
                            ? 'bg-white/5 border-white/10 text-gray-300 hover:border-purple-500/40'
                            : 'bg-white/[0.02] border-white/5 text-gray-600 cursor-not-allowed'
                        }`}
                        data-testid={`deposit-qr-crypto-${c}`}
                        title={available ? '' : 'No address configured'}
                      >
                        <CryptoIcon symbol={c} size={16} className="shrink-0" />
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Network selector */}
              {availableNetworks.length > 0 && (
                <div className="mb-3">
                  <p className="text-gray-400 text-xs mb-2">Network</p>
                  <div className="flex flex-wrap items-center gap-2" data-testid="deposit-qr-network-controls">
                    {availableNetworks.map((n) => {
                      const active = selectedNetwork === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setSelectedNetwork(n)}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors focus:outline-none ${
                            active
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'bg-white/5 border-white/10 text-gray-300 hover:border-emerald-400/40'
                          }`}
                          data-testid={`deposit-qr-network-${n}`}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Amount presets + custom input */}
              <div className="mb-3">
                <p className="text-gray-400 text-xs mb-2">Amount (optional)</p>
                <div className="flex flex-wrap items-center gap-2" data-testid="deposit-qr-amount-controls">
                  {AMOUNT_PRESETS.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setAmount(String(v))}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors focus:outline-none ${
                        String(v) === amount
                          ? 'bg-purple-500 border-purple-500 text-white'
                          : 'bg-white/5 border-white/10 text-gray-300 hover:border-purple-500/40'
                      }`}
                      data-testid={`deposit-qr-amount-preset-${v}`}
                    >
                      ${v}
                    </button>
                  ))}
                  <div className="relative flex-1 min-w-[110px]">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      placeholder="Custom"
                      value={amount}
                      onChange={(e) => {
                        // Strip non-numeric characters then collapse repeated
                        // decimal points so state can't hold '10.5.3'.
                        const cleaned = e.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
                        setAmount(cleaned);
                      }}
                      className="w-full pl-6 pr-8 py-1.5 rounded-full bg-white/5 border border-white/10 text-white text-xs font-semibold focus:outline-none focus:border-purple-500/60"
                      aria-label={`Custom deposit amount ${selectedCrypto}`}
                      data-testid="deposit-qr-amount-input"
                      min="0"
                      step="0.01"
                    />
                    {amount && (
                      <button
                        type="button"
                        onClick={() => setAmount('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-white/10 focus:outline-none"
                        aria-label="Clear amount"
                        data-testid="deposit-qr-amount-clear"
                      >
                        <X className="h-3 w-3 text-gray-400" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* QR */}
              <div className="mx-auto w-fit p-3 bg-white rounded-2xl shadow-inner" data-testid="deposit-qr-image-wrapper">
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt={`${selectedCrypto} deposit QR`}
                    width={240}
                    height={240}
                    data-testid="deposit-qr-image"
                    style={{ display: 'block' }}
                  />
                ) : (
                  <div style={{ width: 240, height: 240 }} className="flex items-center justify-center text-gray-400 text-sm">
                    {currentAddress ? 'Generating…' : 'No address configured'}
                  </div>
                )}
              </div>
              <p className="text-center text-gray-400 text-xs mt-3" data-testid="deposit-qr-caption">
                {amountIsValid
                  ? `Send exactly $${amountNumber.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${selectedCrypto}`
                  : `Scan this from your wallet app to send ${selectedCrypto}`}
              </p>

              {/* Address + Copy */}
              <div className="pt-4 pb-5">
                <div className="rounded-xl bg-gray-900/70 border border-gray-800 p-3 flex items-center gap-2">
                  <p className="text-white text-xs font-mono flex-1 break-all leading-relaxed" data-testid="deposit-qr-address">
                    {currentAddress || 'Address unavailable'}
                  </p>
                  <button
                    type="button"
                    onClick={handleCopy}
                    disabled={!currentAddress}
                    className={`shrink-0 px-3 py-2 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition-colors focus:outline-none disabled:opacity-40 ${
                      copied ? 'bg-emerald-500 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white'
                    }`}
                    aria-label={`Copy ${selectedCrypto} address`}
                    data-testid="deposit-qr-copy-btn"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>

                <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                  <ArrowDownToLine className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    Send {selectedCrypto} on <strong>{selectedNetwork || '—'}</strong> only. Other chains will lose funds permanently.
                    {amountIsValid && ' Amount hint is encoded into the QR — some wallets pre-fill it automatically.'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default CopyDepositAddressCard;
